/**
 * The asset catalog: list and search, one asset, its history and its kind's template, and every write.
 *
 * Every write sends the asset's `revision` and answers the next one; the answer goes into the cached asset at once,
 * so the editor keeps saving without a reload. A stale revision is a 409 (`isStaleRevision`), which the screens
 * answer with a Reload notice.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../client';
import { queryKeys } from './keys';
import type {
  AssetCreateIn,
  AssetListOut,
  AssetListParams,
  AssetOut,
  AssetUpdateIn,
  CatalogCard,
  HistoryOut,
  RelationIn,
  RelationsOut,
  RevisionOut,
  SectionIn,
  SectionSaveOut,
  SectionsOut,
  TemplateOut,
} from '../types';

/** The query string of `GET /api/assets`: lists as repeated params, defaults left out. */
export function assetListSearch(params: AssetListParams): string {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  for (const key of ['kind', 'status', 'maturity', 'industry', 'partner', 'country'] as const) {
    for (const value of params[key] ?? []) query.append(key, value);
  }
  for (const key of ['stale', 'sort', 'order', 'page', 'page_size'] as const) {
    const value = params[key];
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : '';
}

export function useAssets(params: AssetListParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.assets(params),
    queryFn: () => api<AssetListOut>(`/api/assets${assetListSearch(params)}`),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useAsset(id: string) {
  return useQuery({
    queryKey: queryKeys.asset(id),
    queryFn: () => api<AssetOut>(`/api/assets/${id}`),
  });
}

export function useAssetHistory(id: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: queryKeys.assetHistory(id, page, pageSize),
    queryFn: () => api<HistoryOut>(`/api/assets/${id}/history?page=${page}&page_size=${pageSize}`),
    placeholderData: (previous) => previous,
  });
}

/** The kind's suggested sections; fixed in code, so read once per session. */
export function useAssetTemplate(kind: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.assetTemplate(kind),
    queryFn: () => api<TemplateOut>(`/api/catalog/templates/${kind}`),
    enabled: enabled && kind !== '',
    staleTime: Infinity,
  });
}

export function useCatalogCard({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.catalogCard,
    queryFn: () => api<CatalogCard>('/api/dashboard/catalog'),
    enabled,
  });
}

/** True for the 409 a write gets when someone else saved the asset first. */
export function isStaleRevision(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && /revision/i.test(error.message);
}

/**
 * Lists, history and the dashboard card change with any write; the asset itself is set from the answer. The partner
 * list too: a save can create a partner name, and the list holds only names some asset carries.
 */
function invalidateAround(client: QueryClient, id: string) {
  void client.invalidateQueries({ queryKey: ['assets', 'list'] });
  void client.invalidateQueries({ queryKey: queryKeys.partners });
  void client.invalidateQueries({ queryKey: ['assets', 'history', id] });
  void client.invalidateQueries({ queryKey: queryKeys.catalogCard });
}

function patchCached(client: QueryClient, id: string, patch: (asset: AssetOut) => AssetOut) {
  client.setQueryData<AssetOut>(queryKeys.asset(id), (asset) => (asset ? patch(asset) : asset));
}

function useAssetWrite() {
  const client = useQueryClient();
  return {
    client,
    /** A whole asset came back: cache it. */
    replace: (asset: AssetOut) => {
      client.setQueryData(queryKeys.asset(asset.id), asset);
      invalidateAround(client, asset.id);
    },
    /** Part of an asset came back: merge it, then re-read the asset for what the server derives (publish_missing). */
    merge: (id: string, patch: (asset: AssetOut) => AssetOut) => {
      patchCached(client, id, patch);
      void client.invalidateQueries({ queryKey: queryKeys.asset(id) });
      invalidateAround(client, id);
    },
  };
}

export function useCreateAsset() {
  const { replace } = useAssetWrite();
  return useMutation({
    meta: { success: (asset: AssetOut) => `${asset.name} created as a draft` },
    mutationFn: (body: AssetCreateIn) => api<AssetOut>('/api/assets', 'POST', body),
    onSuccess: replace,
  });
}

export function useUpdateAsset(id: string) {
  const { replace } = useAssetWrite();
  return useMutation({
    meta: { success: (asset: AssetOut) => `${asset.name} saved` },
    mutationFn: (body: AssetUpdateIn) => api<AssetOut>(`/api/assets/${id}`, 'PATCH', body),
    onSuccess: replace,
  });
}

export type LifecycleAction = 'publish' | 'archive' | 'unarchive' | 'confirm';

const LIFECYCLE_DONE: Record<LifecycleAction, (name: string) => string> = {
  publish: (name) => `${name} published`,
  archive: (name) => `${name} archived`,
  unarchive: (name) => `${name} is a draft again`,
  confirm: (name) => `${name} confirmed as still accurate`,
};

export function useAssetLifecycle(id: string) {
  const { replace } = useAssetWrite();
  return useMutation({
    meta: {
      success: (asset: AssetOut, { action }: { action: LifecycleAction }) => LIFECYCLE_DONE[action](asset.name),
      failure: 'Could not change the asset',
    },
    mutationFn: ({ action, revision }: { action: LifecycleAction; revision: number }) =>
      api<AssetOut>(`/api/assets/${id}/${action}`, 'POST', { revision }),
    onSuccess: replace,
  });
}

export function useSaveSection(id: string) {
  const { merge } = useAssetWrite();
  return useMutation({
    meta: {
      success: (answer: SectionSaveOut) =>
        answer.warnings.length > 0 ? `${answer.section.title} saved, with a warning` : `${answer.section.title} saved`,
    },
    mutationFn: ({ key, body }: { key: string; body: SectionIn }) =>
      api<SectionSaveOut>(`/api/assets/${id}/sections/${encodeURIComponent(key)}`, 'PUT', body),
    onSuccess: (answer, { key }) =>
      merge(id, (asset) => ({
        ...asset,
        revision: answer.revision,
        sections: [...asset.sections.filter((s) => s.key !== key), answer.section].sort((a, b) => a.position - b.position),
      })),
  });
}

export function useRemoveSection(id: string) {
  const { merge } = useAssetWrite();
  return useMutation({
    meta: { success: (_: RevisionOut, { title }: { key: string; title: string; revision: number }) => `${title} removed` },
    mutationFn: ({ key, revision }: { key: string; title: string; revision: number }) =>
      api<RevisionOut>(`/api/assets/${id}/sections/${encodeURIComponent(key)}?revision=${revision}`, 'DELETE'),
    onSuccess: (answer, { key }) =>
      merge(id, (asset) => ({ ...asset, revision: answer.revision, sections: asset.sections.filter((s) => s.key !== key) })),
  });
}

export function useOrderSections(id: string) {
  const { merge } = useAssetWrite();
  return useMutation({
    meta: { success: 'Section order saved' },
    mutationFn: ({ keys, revision }: { keys: string[]; revision: number }) =>
      api<SectionsOut>(`/api/assets/${id}/sections/order`, 'POST', { keys, revision }),
    onSuccess: (answer) => merge(id, (asset) => ({ ...asset, revision: answer.revision, sections: answer.sections })),
  });
}

export function useSaveRelations(id: string) {
  const { merge } = useAssetWrite();
  return useMutation({
    meta: { success: 'Related assets saved' },
    mutationFn: ({ relations, revision }: { relations: RelationIn[]; revision: number }) =>
      api<RelationsOut>(`/api/assets/${id}/relations`, 'PUT', { relations, revision }),
    onSuccess: (answer) => merge(id, (asset) => ({ ...asset, revision: answer.revision, relations: answer.relations })),
  });
}
